import type {
  Catalogs, Task, CreateTaskDto, UpdateTaskDto, MoveTaskDto,
  BitacoraEntry, ChecklistEntry, ChecklistTipo, PlantillaTipo, GroupBy,
  PlantillaTarea, PlantillaTareaSummary, PlantillaTareaItem,
  CreatePlantillaTareaDto, UpdatePlantillaTareaDto,
  CreatePlantillaTareaItemDto, UpdatePlantillaTareaItemDto,
  Category, CreateCategoryDto, UpdateCategoryDto, CategoryUsage,
  Fase, EstadoLote, LoteKanban, LoteHistoricoEvento, Modelo, ModeloConCroquis,
  CreateFaseDto, UpdateFaseDto, FaseUsage,
  CreateEstadoLoteDto, UpdateEstadoLoteDto, EstadoLoteUsage,
  Sprint, CreateSprintDto, UpdateSprintDto, SprintTipo,
  CapacidadDefault, CapacidadOverride, EffectiveCapacidad, NotifPref,
  Dashboard, PokerVotacion, TimelineEvento, FeedPage, TimelineEventoTipo,
  PorVincular, VincularRequest, VincularResult, LoteBusqueda,
  ReTramiteRequest, ReTramiteResult,
  AutoVincularRequest, AutoVincularResult, PorVerificar,
  VerificarRequest, VerificarResult,
  Dependencia, CreateDependenciaDto
} from '@/types';
import type { LoteSVG, ProyectoCode } from '@/types/sitePlan';
import type { ModeloPlantaCrudo } from '@/components/planta/tipos';
import type { AjustesPlanta, ObjetoPlantaDto, ObjetoPlantaEntrada } from '@/components/planta/objetos-servidor';
import { getAccessToken } from '@/auth/getAccessToken';

const BASE = `${import.meta.env.VITE_API_BASE_URL || ''}/api`;

// Error enriquecido con el status HTTP + el cuerpo parseado de la respuesta.
// Permite a los callers distinguir casos estructurados — p.ej. el 409 de
// re-tramite, que trae { mensaje, tramiteVigente } en vez de { error }.
// `.message` sigue seteado, asi que los catchers que solo leen err.message
// no cambian de comportamiento.
export interface ApiError extends Error {
  status: number;
  body: unknown;
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> || {})
  };

  // Attach Bearer token if auth is enabled. getAccessToken returns null in DevMode.
  const token = await getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, { ...opts, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(
      (body as { error?: string }).error
      || (body as { mensaje?: string }).mensaje
      || `HTTP ${res.status}`
    ) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// Subir un archivo crudo. No puede pasar por `request` porque ese fuerza
// `Content-Type: application/json` y acá el cuerpo son bytes. Comparte lo que
// importa: la misma base, el mismo Bearer y la misma forma de `ApiError`, para
// que el caller distinga el 400 «esta lámina no se puede leer» —que viene con
// su motivo y hay que mostrárselo a quien arrastró el PDF— del 502 «el
// convertidor no contesta», que no es culpa del archivo.
async function postArchivo<T>(path: string, archivo: Blob, tipo: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': tipo };
  const token = await getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: archivo });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(
      (body as { error?: string }).error || `HTTP ${res.status}`
    ) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
}

// Bajar bytes con el mismo Bearer que el resto. `request` no sirve porque
// hace `res.json()`, y acá lo que vuelve es un PDF. Mismo `ApiError` para que
// el caller pueda leer el motivo del 404 («ese modelo no tiene croquis»).
async function getBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = await getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { headers, signal });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(
      (body as { error?: string }).error || `HTTP ${res.status}`
    ) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.blob();
}

export const api = {
  catalogs: () => request<Catalogs>('/catalogs'),
  tasks: {
    list: (params: { groupBy?: GroupBy; search?: string; projectId?: number; assigneeId?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.groupBy) qs.set('groupBy', params.groupBy);
      if (params.search) qs.set('search', params.search);
      if (params.projectId) qs.set('projectId', String(params.projectId));
      if (params.assigneeId) qs.set('assigneeId', String(params.assigneeId));
      const query = qs.toString();
      return request<Task[]>(`/tasks${query ? '?' + query : ''}`);
    },
    get: (id: number) => request<Task>(`/tasks/${id}`),
    create: (dto: CreateTaskDto) => request<Task>('/tasks', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: number, dto: UpdateTaskDto) =>
      request<Task>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    remove: (id: number) => request<void>(`/tasks/${id}`, { method: 'DELETE' }),
    move: (id: number, dto: MoveTaskDto) =>
      request<Task>(`/tasks/${id}/move`, { method: 'PATCH', body: JSON.stringify(dto) }),
    // Bulk-update del sprint. idSprint = null limpia la asignacion. Devuelve
    // el conteo de tareas efectivamente actualizadas (skipea las que ya
    // tenian ese sprint).
    bulkUpdateSprint: (taskIds: number[], idSprint: number | null) =>
      request<{ actualizadas: number }>('/tasks/bulk-update-sprint', {
        method: 'POST',
        body: JSON.stringify({ taskIds, idSprint })
      }),
    // Round-robin del autorizador para una tarea nueva: backend devuelve el
    // candidato con menor carga de revisiones activas, excluyendo al
    // responsable. Si no hay candidatos viables, idCol viene null.
    suggestAuthorizer: (responsableId: number | null, signal?: AbortSignal) => {
      const qs = responsableId !== null && responsableId !== undefined
        ? `?responsableId=${responsableId}`
        : '';
      return request<{ idCol: number | null; nombreCompleto: string | null; currentLoad: number }>(
        `/tasks/suggest-authorizer${qs}`,
        { signal }
      );
    }
  },
  bitacora: {
    list: (taskId: number) => request<BitacoraEntry[]>(`/tasks/${taskId}/bitacora`),
    create: (taskId: number, comentario: string, tipoComentario?: string) =>
      request<BitacoraEntry>(`/tasks/${taskId}/bitacora`, {
        method: 'POST', body: JSON.stringify({ comentario, tipoComentario })
      })
  },
  checklist: {
    list: (taskId: number) => request<ChecklistEntry[]>(`/tasks/${taskId}/checklist`),
    create: (taskId: number, texto: string, opts: { descripcion?: string; tipo?: ChecklistTipo } = {}) =>
      request<ChecklistEntry>(`/tasks/${taskId}/checklist`, {
        method: 'POST', body: JSON.stringify({ texto, descripcion: opts.descripcion, tipo: opts.tipo })
      }),
    update: (taskId: number, itemId: number, patch: { texto?: string; descripcion?: string; completado?: boolean; autorizado?: boolean }) =>
      request<ChecklistEntry>(`/tasks/${taskId}/checklist/${itemId}`, {
        method: 'PATCH', body: JSON.stringify(patch)
      }),
    remove: (taskId: number, itemId: number) =>
      request<void>(`/tasks/${taskId}/checklist/${itemId}`, { method: 'DELETE' }),
    // tipo: obligatorio cuando la plantilla origen es 'ambas' (el backend
    // valida y devuelve 400 si falta). Para plantillas de tipo concreto se
    // puede omitir; el backend cae al Tipo de la plantilla.
    loadTemplate: (
      taskId: number,
      plantillaId: number,
      opts: { replaceExisting?: boolean; tipo?: ChecklistTipo } = {}
    ) =>
      request<ChecklistEntry[]>(
        `/tasks/${taskId}/checklist/load-template/${plantillaId}`,
        { method: 'POST', body: JSON.stringify({
          replaceExisting: opts.replaceExisting ?? false,
          tipo: opts.tipo,
        }) }
      ),
    saveAsTemplate: (
      taskId: number,
      dto: { nombre: string; idCategoria: number; tipo: PlantillaTipo }
    ) =>
      request<PlantillaTarea>(`/tasks/${taskId}/checklist/save-as-template`, {
        method: 'POST', body: JSON.stringify(dto)
      })
  },
  plantillas: {
    list: (params: { categoryId?: number; tipo?: PlantillaTipo; activo?: boolean } = {}) => {
      const qs = new URLSearchParams();
      if (params.categoryId !== undefined) qs.set('categoryId', String(params.categoryId));
      if (params.tipo) qs.set('tipo', params.tipo);
      if (params.activo !== undefined) qs.set('activo', String(params.activo));
      const q = qs.toString();
      return request<PlantillaTareaSummary[]>(`/plantillas${q ? '?' + q : ''}`);
    },
    get: (id: number) => request<PlantillaTarea>(`/plantillas/${id}`),
    create: (dto: CreatePlantillaTareaDto) =>
      request<PlantillaTarea>('/plantillas', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: number, dto: UpdatePlantillaTareaDto) =>
      request<PlantillaTarea>(`/plantillas/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    remove: (id: number) =>
      request<void>(`/plantillas/${id}`, { method: 'DELETE' }),
    duplicate: (id: number) =>
      request<PlantillaTarea>(`/plantillas/${id}/duplicate`, { method: 'POST' }),
    items: {
      create: (plantillaId: number, dto: CreatePlantillaTareaItemDto) =>
        request<PlantillaTareaItem>(`/plantillas/${plantillaId}/items`, {
          method: 'POST', body: JSON.stringify(dto)
        }),
      update: (plantillaId: number, itemId: number, dto: UpdatePlantillaTareaItemDto) =>
        request<PlantillaTareaItem>(`/plantillas/${plantillaId}/items/${itemId}`, {
          method: 'PATCH', body: JSON.stringify(dto)
        }),
      remove: (plantillaId: number, itemId: number) =>
        request<void>(`/plantillas/${plantillaId}/items/${itemId}`, { method: 'DELETE' }),
      reorder: (plantillaId: number, orderedIds: number[]) =>
        request<void>(`/plantillas/${plantillaId}/items/reorder`, {
          method: 'POST', body: JSON.stringify({ orderedIds })
        })
    }
  },
  categories: {
    list: () => request<Category[]>('/categories'),
    usage: (id: number) => request<CategoryUsage>(`/categories/${id}/usage`),
    create: (dto: CreateCategoryDto) =>
      request<Category>('/categories', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: number, dto: UpdateCategoryDto) =>
      request<Category>(`/categories/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    remove: (id: number) =>
      request<void>(`/categories/${id}`, { method: 'DELETE' }),
    reorder: (orderedIds: number[]) =>
      request<void>('/categories/reorder', {
        method: 'POST', body: JSON.stringify({ orderedIds })
      })
  },
  sitePlan: {
    lotes: (proyecto: ProyectoCode, signal?: AbortSignal) =>
      request<LoteSVG[]>(`/projects/${proyecto}/lots-svg`, { signal })
  },
  planta: {
    // El croquis de un modelo → sus paredes editables. El backend solo hace de
    // puente al convertidor de Python (`planta-tool/`), así que lo que vuelve
    // es el modelo crudo del importador, igual que los módulos ya medidos de
    // `components/planta/medidos/`.
    convertir: (archivo: File) =>
      api.planta.convertirBytes(archivo, archivo.name),

    // Igual que `convertir` pero con el nombre aparte, para el croquis que bajó
    // del blob: ahí no hay `File` con `.name`, son bytes y el nombre lo dijo el
    // catálogo de modelos. El nombre importa porque el convertidor lo devuelve
    // en el modelo y `fuente.ts` empareja el catálogo medido por él.
    convertirBytes: (bytes: Blob, nombre: string) =>
      postArchivo<ModeloPlantaCrudo>(
        `/planta/convertir?nombre=${encodeURIComponent(nombre)}`,
        bytes,
        'application/pdf'
      ),

    // Los modelos activos que tienen croquis, para el dropdown del editor.
    modelos: (signal?: AbortSignal) =>
      request<ModeloConCroquis[]>('/planta/modelos', { signal }),

    // La planta dibujada → la lámina renderizada. Devuelve el PNG, no JSON.
    //
    // No pasa por `postArchivo` porque eso manda UN cuerpo crudo y acá van dos
    // archivos (la planta y, si hay, la referencia de estilo) más la proporción,
    // que el backend usa para elegir el tamaño de salida. Y no pasa por
    // `getBlob` porque es POST.
    //
    // ⚠️ Adentro son DOS pasos, no uno: el POST encola y vuelve en un segundo
    // con un `jobId`, y después se pregunta cada 3 s hasta que la lámina está.
    // Fue a la fuerza: la versión que sostenía una sola petición de 90 s moría
    // en el proxy del Static Web App a los ~85 s (`resultCode` 499, medido el
    // 14-oct-2026) y también cada vez que el celular cambiaba de red o apagaba
    // la pantalla — con el crédito de OpenAI ya gastado. Preguntar es lo único
    // que sobrevive a eso.
    //
    // La firma no cambió a propósito: para quien llama sigue siendo «dame el
    // PNG» y el que espera es este método, no la pantalla.
    //
    // El `signal` no es decorativo: un render tarda 40-90 s y quien lo lanzó
    // tiene que poder cerrar el editor sin dejar la espera colgada.
    render: async (planta: Blob, ancho: number, alto: number, estilo?: Blob, signal?: AbortSignal) => {
      const fd = new FormData();
      fd.append('planta', planta, 'planta.png');
      fd.append('ancho', String(ancho));
      fd.append('alto', String(alto));
      if (estilo) fd.append('estilo', estilo, 'estilo.png');

      const cabeceras = async (): Promise<Record<string, string>> => {
        const token = await getAccessToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      };

      const reventar = async (res: Response): Promise<never> => {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        const err = new Error(
          (body as { error?: string }).error || `HTTP ${res.status}`
        ) as ApiError;
        err.status = res.status;
        err.body = body;
        throw err;
      };

      // Paso 1 — encolar. Sin `Content-Type` a mano: el navegador tiene que
      // ponerlo él para incluir el `boundary` del multipart. Escribirlo rompe
      // el parseo en el backend con un 400 que no dice nada.
      const alta = await fetch(`${BASE}/planta/render`, {
        method: 'POST', headers: await cabeceras(), body: fd, signal
      });
      if (!alta.ok) await reventar(alta);
      const { jobId } = (await alta.json()) as { jobId: string };

      // Paso 2 — preguntar. El token se pide en cada vuelta porque una espera
      // larga puede cruzar su vencimiento, y MSAL lo renueva solo.
      const ESPERA_MS = 3000;
      const TOPE_MS = 6 * 60 * 1000; // bien arriba del render más lento visto
      const limite = Date.now() + TOPE_MS;

      for (;;) {
        await new Promise<void>((ok, fail) => {
          const t = setTimeout(ok, ESPERA_MS);
          signal?.addEventListener('abort', () => {
            clearTimeout(t);
            fail(new DOMException('Aborted', 'AbortError'));
          }, { once: true });
        });

        const res = await fetch(`${BASE}/planta/render/${jobId}`, {
          headers: await cabeceras(), signal
        });
        if (!res.ok) await reventar(res);

        // El backend contesta JSON mientras espera y el PNG cuando terminó: el
        // `content-type` es el que dice en qué paso estamos.
        if ((res.headers.get('content-type') || '').includes('image/')) {
          return res.blob();
        }

        const estado = (await res.json()) as { valor?: string; error?: string; detalle?: string };
        if (estado.valor === 'error') {
          const err = new Error(estado.error || 'No se pudo generar la lámina.') as ApiError;
          err.status = 500;
          err.body = estado;
          throw err;
        }

        if (Date.now() > limite) {
          throw new Error('El render está tardando demasiado. Volvé a intentarlo.');
        }
      }
    },

    // Los bytes del croquis, servidos por el backend desde el mismo origen.
    // Pasa por acá y no directo al blob porque `expcliente.blob.core.windows.net`
    // tiene lista blanca de CORS sin `arquiflow.adelante.cr`: pdfjs baja el PDF
    // con fetch, así que el navegador lo bloquea antes de que llegue a pintarse.
    croquis: (idModelo: number, signal?: AbortSignal) =>
      getBlob(`/planta/croquis/${idModelo}`, signal)
  },
  fases: {
    list: () => request<Fase[]>('/fases'),
    usage: (id: number) => request<FaseUsage>(`/fases/${id}/usage`),
    create: (dto: CreateFaseDto) =>
      request<Fase>('/fases', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: number, dto: UpdateFaseDto) =>
      request<Fase>(`/fases/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    remove: (id: number) =>
      request<void>(`/fases/${id}`, { method: 'DELETE' }),
    reorder: (orderedIds: number[]) =>
      request<void>('/fases/reorder', {
        method: 'POST', body: JSON.stringify({ orderedIds })
      })
  },
  lotes: {
    // Kanban de tramites: una tarjeta por lote del proyecto. Proyecto
    // requerido por ahora (VN, VI o VB), mismo patron que SitePlan.
    kanbanList: (proyecto: ProyectoCode, signal?: AbortSignal) =>
      request<LoteKanban[]>(`/lotes-kanban?proyecto=${proyecto}`, { signal }),
    // Mover el lote a otro estado: backend persiste idFaseLote + escribe
    // fila en LoteEstadoHistorico y devuelve la tarjeta actualizada para
    // que el cliente la reemplace in-place (sin refetch completo).
    changeEstado: (idLote: number, idEstadoLote: number) =>
      request<LoteKanban>(`/lotes/${idLote}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ idEstadoLote })
      }),
    // Timeline unificado: cambios de estado + cambios de campos auditados
    // (mas reciente primero). Lo consume el modal de detalle del lote.
    historico: (idLote: number) =>
      request<LoteHistoricoEvento[]>(`/lotes/${idLote}/historico`),
    // Setea/limpia el modelo tramitado del lote. Body { idModDefecto: number | null }.
    // Devuelve la tarjeta actualizada (el backend resuelve el nombre del
    // modelo via V_LotesSVG en la misma respuesta).
    changeModeloTramitado: (idLote: number, idModDefecto: number | null) =>
      request<LoteKanban>(`/lotes/${idLote}/modelo-tramitado`, {
        method: 'PATCH',
        body: JSON.stringify({ idModDefecto })
      }),
    // Codigo del tramite CFIA. Strings vacios/blancos los backend
    // normaliza a null (limpia la columna).
    changeCodigoApc: (idLote: number, codigoApc: string | null) =>
      request<LoteKanban>(`/lotes/${idLote}/codigo-apc`, {
        method: 'PATCH',
        body: JSON.stringify({ codigoApc })
      }),
    // Codigo del permiso de construccion municipal (ej. "PC 221-2024").
    changePermisoConstruccion: (idLote: number, permisoConstruccion: string | null) =>
      request<LoteKanban>(`/lotes/${idLote}/permiso-construccion`, {
        method: 'PATCH',
        body: JSON.stringify({ permisoConstruccion })
      })
  },
  modelos: {
    // Filtrado por proyecto (Activo=true AND ActivoV<X>=true). El catalogo
    // entero por proyecto es relativamente chico (decenas de filas),
    // se cachea con staleTime largo en el hook.
    list: (proyecto: ProyectoCode | 'VC') =>
      request<Modelo[]>(`/modelos?proyecto=${proyecto}`)
  },
  // Dependencias (vinculos) entre tareas para el Gantt. source = predecesora,
  // target = sucesora, type = e2s/s2s/e2e/s2e.
  dependencias: {
    list: () => request<Dependencia[]>('/dependencias'),
    create: (dto: CreateDependenciaDto) =>
      request<Dependencia>('/dependencias', { method: 'POST', body: JSON.stringify(dto) }),
    remove: (id: number) =>
      request<void>(`/dependencias/${id}`, { method: 'DELETE' })
  },
  bootstrap: {
    // Bandeja de bootstrap CFIA (B3): correos PorVincular agrupados por
    // codigo, en dos cubos (con/sin candidato del motor). Lectura pura.
    porVincular: () => request<PorVincular>('/bootstrap/por-vincular'),
    // Vincular el grupo a un lote -> crea el tramite en su estado inicial
    // (todo server-side). Si el lote ya tiene tramite vigente, request() lanza
    // un ApiError con status=409 y body=ReTramiteRequerido -> el caller lo
    // maneja (PR 1 muestra el mensaje; el dialogo de re-tramite es del PR 2).
    vincular: (body: VincularRequest) =>
      request<VincularResult>('/bootstrap/vincular', {
        method: 'POST', body: JSON.stringify(body)
      }),
    // Buscador manual (escape de 9.4): por IDBD o identificador, scope v1,
    // tope 25. q < 2 chars -> el backend devuelve vacio.
    buscarLotes: (q: string) =>
      request<LoteBusqueda[]>(`/bootstrap/buscar-lotes?q=${encodeURIComponent(q)}`),
    // Re-tramite explicito (9.6): solo tras el 409 del vincular, con el camino
    // que el humano eligio viendo el tramite viejo. 'nuevo' = demote-then-promote;
    // 'mismo' = asocia los correos al vigente. 409 (concurrencia / lote sin
    // vigente) -> ApiError con status+body, el dialogo lo muestra adentro.
    reTramite: (body: ReTramiteRequest) =>
      request<ReTramiteResult>('/bootstrap/re-tramite', {
        method: 'POST', body: JSON.stringify(body)
      }),
    // B4: auto-vínculo con salvaguarda. `dryRun: true` CUENTA sin escribir —
    // es el paso previo obligatorio antes de tocar datos reales. `limite`
    // permite una corrida chica primero. Sin dryRun escribe trámites de verdad.
    autoVincular: (body: AutoVincularRequest = {}) =>
      request<AutoVincularResult>('/bootstrap/auto-vincular', {
        method: 'POST', body: JSON.stringify(body)
      }),
    // B4: la cola de verificación (9.5). Trámites vigentes que creó el
    // auto-vínculo y que ningún humano firmó todavía.
    porVerificar: () => request<PorVerificar[]>('/bootstrap/por-verificar'),
    // B4: resolver uno de la cola. 'confirmar' lo firma; 'rechazar' lo jubila
    // y devuelve sus correos a la bandeja. 409 si otro operador ya lo resolvió.
    verificar: (body: VerificarRequest) =>
      request<VerificarResult>('/bootstrap/verificar', {
        method: 'POST', body: JSON.stringify(body)
      })
  },
  sprints: {
    // Lista de sprints. Activos=true por default (oculta archivados).
    list: (params: { activos?: boolean; tipo?: SprintTipo } = {}) => {
      const qs = new URLSearchParams();
      if (params.activos !== undefined) qs.set('activos', String(params.activos));
      if (params.tipo) qs.set('tipo', params.tipo);
      const q = qs.toString();
      return request<Sprint[]>(`/sprints${q ? '?' + q : ''}`);
    },
    // Sprint semanal vigente, o sprint cero "Sin Asignar" como fallback.
    current: () => request<Sprint>('/sprints/current'),
    create: (dto: CreateSprintDto) =>
      request<Sprint>('/sprints', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: number, dto: UpdateSprintDto) =>
      request<Sprint>(`/sprints/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    // Rollover manual del sprint semanal. El Timer Function lo dispara los
    // lunes 06:00 hora CR automaticamente; este endpoint es para replay
    // manual / smoke test. targetDate opcional para forzar una fecha
    // distinta a "hoy".
    closeAndRollover: (targetDate?: string) =>
      request<{
        previousSprint: Sprint | null;
        nextSprint: Sprint;
        tareasArrastradas: number;
        createdNewSprint: boolean;
      }>('/sprints/close-and-rollover', {
        method: 'POST',
        body: JSON.stringify(targetDate ? { targetDate } : {})
      }),
    // Asegura (idempotente) el sprint semanal de la semana de `date`, sin
    // cerrar el anterior ni arrastrar tareas. Lo usa el modo planificacion
    // para resolver el sprint objetivo (proximo lunes) sin efectos del lunes.
    ensureWeekly: (date?: string) =>
      request<Sprint>('/sprints/ensure-weekly', {
        method: 'POST',
        body: JSON.stringify(date ? { targetDate: date } : {})
      })
  },
  poker: {
    // Devuelve la votacion mas reciente (abierta o cerrada) o null.
    get: (taskId: number) =>
      request<PokerVotacion | null>(`/tasks/${taskId}/poker`),
    // Inicia una votacion nueva. Escala opcional (default fibonacci en backend).
    iniciar: (taskId: number, escala?: number[]) =>
      request<PokerVotacion>(`/tasks/${taskId}/poker/iniciar`, {
        method: 'POST',
        body: JSON.stringify(escala ? { escala } : {})
      }),
    // Upsert del voto. Para "?" mandar { valor: null, esInterrogante: true }.
    votar: (taskId: number, valor: number | null, esInterrogante: boolean) =>
      request<PokerVotacion>(`/tasks/${taskId}/poker/voto`, {
        method: 'POST',
        body: JSON.stringify({ valor, esInterrogante })
      }),
    // Cierra la votacion abierta -> calcula mediana -> escribe HorasEstimadas.
    cerrar: (taskId: number) =>
      request<PokerVotacion>(`/tasks/${taskId}/poker/cerrar`, { method: 'POST' })
  },
  timeline: {
    // Timeline de UNA tarea: comentarios + cambios + creacion, ordenados
    // por fecha desc, con cambios del mismo timestamp+actor agrupados.
    forTask: (taskId: number) =>
      request<TimelineEvento[]>(`/tasks/${taskId}/timeline`),
  },
  feed: {
    // Feed global del equipo. Filtros opcionales: idCol del actor, tipo
    // (comentario|cambio|creacion). offset/limit para paginar.
    get: (params: { limit?: number; offset?: number; idCol?: number; tipo?: TimelineEventoTipo } = {}) => {
      const qs = new URLSearchParams();
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      if (params.idCol !== undefined) qs.set('idCol', String(params.idCol));
      if (params.tipo) qs.set('tipo', params.tipo);
      const q = qs.toString();
      return request<FeedPage>(`/feed${q ? '?' + q : ''}`);
    },
  },
  dashboard: {
    // Resumen por colaborador para un sprint. Si idSprint omitido, el
    // backend usa el semanal vigente con fallback al sprint cero.
    get: (idSprint?: number) => {
      const qs = idSprint !== undefined ? `?idSprint=${idSprint}` : '';
      return request<Dashboard>(`/dashboard${qs}`);
    }
  },
  capacidades: {
    // Lista de defaults (1 fila por colaborador con capacidad seteada).
    listDefaults: () => request<CapacidadDefault[]>('/capacidades'),
    // Upsert default. Body { horasDefault, horasRevision } (0-168 cada uno).
    upsertDefault: (idCol: number, horasDefault: number, horasRevision: number) =>
      request<CapacidadDefault>(`/capacidades/${idCol}`, {
        method: 'PUT',
        body: JSON.stringify({ horasDefault, horasRevision })
      }),
    // Capacidad efectiva por colaborador para el sprint. Combina default +
    // override + horas asignadas (suma de HorasEstimadas de tareas activas
    // del sprint con ese responsable). El frontend lo usa en el indicador
    // del kanban /assignee y en el dashboard.
    effective: (idSprint: number) =>
      request<EffectiveCapacidad[]>(`/capacidades/sprint/${idSprint}`),
    // Upsert override por sprint+colaborador. Body { horasOverride: number }.
    upsertOverride: (idSprint: number, idCol: number, horasOverride: number) =>
      request<CapacidadOverride>(`/capacidades/sprint/${idSprint}/colaborador/${idCol}`, {
        method: 'PUT',
        body: JSON.stringify({ horasOverride })
      }),
    // Borra el override (la capacidad vuelve al HorasDefault del colaborador).
    deleteOverride: (idSprint: number, idCol: number) =>
      request<void>(`/capacidades/sprint/${idSprint}/colaborador/${idCol}`, {
        method: 'DELETE'
      })
  },
  // Preferencias de notificaciones de Teams del usuario actual (self-service).
  notifPrefs: {
    get: () => request<NotifPref>('/notification-prefs'),
    update: (prefs: NotifPref) =>
      request<NotifPref>('/notification-prefs', {
        method: 'PUT',
        body: JSON.stringify(prefs)
      })
  },
  estadosLote: {
    // Filtros opcionales en el server: idFase reduce el listado al dropdown
    // que aparece al soltar un lote sobre una columna del kanban.
    list: (params: { idFase?: number; tipoProyecto?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.idFase !== undefined) qs.set('idFase', String(params.idFase));
      if (params.tipoProyecto) qs.set('tipoProyecto', params.tipoProyecto);
      const q = qs.toString();
      return request<EstadoLote[]>(`/estados-lote${q ? '?' + q : ''}`);
    },
    usage: (id: number) => request<EstadoLoteUsage>(`/estados-lote/${id}/usage`),
    create: (dto: CreateEstadoLoteDto) =>
      request<EstadoLote>('/estados-lote', { method: 'POST', body: JSON.stringify(dto) }),
    update: (id: number, dto: UpdateEstadoLoteDto) =>
      request<EstadoLote>(`/estados-lote/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
    remove: (id: number) =>
      request<void>(`/estados-lote/${id}`, { method: 'DELETE' }),
    // Reorder dentro de UNA fase (el server ignora ids de otras fases).
    reorder: (idFase: number, orderedIds: number[]) =>
      request<void>('/estados-lote/reorder', {
        method: 'POST', body: JSON.stringify({ idFase, orderedIds })
      })
  },

  // El catalogo de objetos del editor de plantas. `puedePublicar` sale de
  // ObjetosPlantaPublicador: es el rol, no una preferencia del cliente.
  objetosPlanta: {
    list: () =>
      request<{ objetos: ObjetoPlantaDto[]; puedePublicar: boolean; ajustes: AjustesPlanta }>(
        '/objetos-planta'),
    create: (dto: ObjetoPlantaEntrada) =>
      request<ObjetoPlantaDto>('/objetos-planta', {
        method: 'POST', body: JSON.stringify(dto)
      }),
    update: (clave: string, dto: ObjetoPlantaEntrada) =>
      request<ObjetoPlantaDto>(`/objetos-planta/${encodeURIComponent(clave)}`, {
        method: 'PUT', body: JSON.stringify(dto)
      }),
    remove: (clave: string) =>
      request<void>(`/objetos-planta/${encodeURIComponent(clave)}`, { method: 'DELETE' }),
    publicar: (clave: string) =>
      request<ObjetoPlantaDto>(`/objetos-planta/${encodeURIComponent(clave)}/publicar`, {
        method: 'POST'
      }),
    // Ruta aparte de `/objetos-planta/...` a propósito: como sub-recurso habría
    // competido con `/objetos-planta/{clave}` en el ruteo del host.
    guardarAjustes: (a: AjustesPlanta) =>
      request<AjustesPlanta>('/ajustes-planta', { method: 'PUT', body: JSON.stringify(a) })
  }
};